import { useEffect, useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Check, Copy, Send, Trash2 } from "lucide-react"
import { useActiveEvent } from "@/core/active-event"
import { BACKEND_ORIGIN } from "@/core/backend-url"
import {
  getSlackIntegration,
  removeSlackIntegration,
  setSlackIntegration,
  testSlackIntegration,
} from "@/core/settings"
import { ApiError } from "@/core/api"
import {
  Card,
  Field,
  GhostButton,
  Pill,
  PrimaryButton,
  SettingsPage,
  TextInput,
} from "@/components/settings/primitives"

export const Route = createFileRoute("/$orgSlug/$eventSlug/settings/integrations")({
  component: IntegrationsView,
})

function IntegrationsView() {
  return (
    <SettingsPage
      title="Integrations"
      description="Connect this event to the tools your team already uses."
    >
      <AgentMcpCard />
      <SlackCard />
      <CventCard />
    </SettingsPage>
  )
}

// Connect an AI assistant (Claude / ChatGPT) to Attend over MCP. The server is
// always-on and OAuth-gated — the assistant registers itself (Dynamic Client
// Registration), the organizer signs in + consents, and it acts through that
// account's role/scope. Nothing to configure here beyond pointing the assistant
// at the MCP URL; this card just surfaces it.
function AgentMcpCard() {
  const mcpUrl = `${BACKEND_ORIGIN}/mcp`
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(mcpUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error("Couldn't copy")
    }
  }

  return (
    <Card
      title="Connect Claude or ChatGPT"
      description="Let an AI assistant work your events over MCP — it reads and acts through your own Attend account and permissions."
    >
      <Field
        label="MCP server URL"
        hint="Paste this into your assistant's connector settings — it handles sign-in for you."
      >
        <div className="flex items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-lg bg-plum-100 px-3 py-2 text-[12.5px] text-plum-800 dark:bg-white/10 dark:text-plum-100">
            {mcpUrl}
          </code>
          <GhostButton onClick={copy}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy"}
          </GhostButton>
        </div>
      </Field>

      <div className="space-y-1.5 text-[12.5px] text-plum-700/60 dark:text-plum-200/45">
        <p>
          <strong className="text-plum-800 dark:text-plum-100">Claude:</strong>{" "}
          Settings → Connectors → Add custom connector → paste the URL.
        </p>
        <p>
          <strong className="text-plum-800 dark:text-plum-100">ChatGPT:</strong>{" "}
          Settings → Connectors → add an MCP server → paste the URL.
        </p>
        <p>
          Sign in with your Attend organizer account when prompted and approve access —
          there are no app keys to set up.
        </p>
        <p>
          The assistant can see the events you're a member of and only do what your role
          allows.
        </p>
      </div>
    </Card>
  )
}

function SlackCard() {
  const { eventId } = useActiveEvent()
  const qc = useQueryClient()

  const q = useQuery({
    queryKey: ["integration", "slack", eventId],
    queryFn: () => getSlackIntegration(eventId!),
    enabled: !!eventId,
  })
  const integration = q.data?.integration ?? null

  const [webhookUrl, setWebhookUrl] = useState("")
  const [channelLabel, setChannelLabel] = useState("")
  // Dry-run → confirm: a successful dry-run unlocks the real save. Never assume.
  const [preview, setPreview] = useState<string | null>(null)

  useEffect(() => {
    setChannelLabel(integration?.channelLabel ?? "")
    // Webhook is write-only on the server (we only get a redacted preview), so
    // the input always starts blank — entering a value replaces the secret.
    setWebhookUrl("")
    setPreview(null)
  }, [integration])

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["integration", "slack", eventId] })

  const dryRun = useMutation({
    mutationFn: () => {
      if (!eventId || !webhookUrl.trim())
        throw new ApiError("Paste an incoming webhook URL first", 400)
      return setSlackIntegration({
        eventId,
        webhookUrl: webhookUrl.trim(),
        channelLabel: channelLabel.trim() || null,
        dryRun: true,
      }) as Promise<{ wouldSet?: { webhookPreview?: string } }>
    },
    onSuccess: (r) => {
      setPreview(r.wouldSet?.webhookPreview ?? "Looks valid")
      toast.success("Webhook validated — confirm to save")
    },
    onError: (e) => {
      setPreview(null)
      toast.error(e instanceof ApiError ? e.message : "Webhook validation failed")
    },
  })

  const save = useMutation({
    mutationFn: () => {
      if (!eventId) throw new Error("Not ready")
      return setSlackIntegration({
        eventId,
        webhookUrl: webhookUrl.trim(),
        channelLabel: channelLabel.trim() || null,
      })
    },
    onSuccess: () => {
      setPreview(null)
      setWebhookUrl("")
      toast.success("Slack connected")
      void invalidate()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed to save Slack"),
  })

  const test = useMutation({
    mutationFn: () => testSlackIntegration(eventId!),
    onSuccess: () => toast.success("Test message sent to Slack"),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Test failed"),
  })

  const remove = useMutation({
    mutationFn: () => removeSlackIntegration(eventId!),
    onSuccess: () => {
      toast.success("Slack disconnected")
      void invalidate()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed to disconnect"),
  })

  return (
    <Card
      title="Slack"
      description="Post event alerts to a Slack channel via an incoming webhook."
    >
      <div className="flex items-center gap-2">
        {integration?.enabled ? (
          <Pill tone="accent">Connected</Pill>
        ) : (
          <Pill>Not connected</Pill>
        )}
        {integration?.webhookPreview && (
          <code className="text-[11.5px] text-plum-700/55 dark:text-plum-200/40">
            {integration.webhookPreview}
          </code>
        )}
      </div>

      <Field
        label="Incoming webhook URL"
        hint={
          integration?.enabled
            ? "Leave blank to keep the current webhook. Paste a new one to replace it."
            : "From Slack → Apps → Incoming Webhooks."
        }
      >
        <TextInput
          type="url"
          placeholder="https://hooks.slack.com/services/…"
          value={webhookUrl}
          onChange={(e) => {
            setWebhookUrl(e.target.value)
            setPreview(null)
          }}
        />
      </Field>
      <Field label="Channel label" hint="Optional — for your own reference.">
        <TextInput
          placeholder="#event-ops"
          value={channelLabel}
          onChange={(e) => setChannelLabel(e.target.value)}
        />
      </Field>

      {preview && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-[12.5px] text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
          Validated: {preview} — confirm to save.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {preview ? (
          <PrimaryButton onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Confirm & save"}
          </PrimaryButton>
        ) : (
          <PrimaryButton
            onClick={() => dryRun.mutate()}
            disabled={dryRun.isPending || !webhookUrl.trim()}
          >
            {dryRun.isPending ? "Checking…" : "Validate webhook"}
          </PrimaryButton>
        )}
        {integration?.enabled && (
          <>
            <GhostButton onClick={() => test.mutate()} disabled={test.isPending}>
              <Send className="h-4 w-4" />
              Send test
            </GhostButton>
            <GhostButton
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              className="text-rose-600 dark:text-rose-400"
            >
              <Trash2 className="h-4 w-4" />
              Disconnect
            </GhostButton>
          </>
        )}
      </div>
    </Card>
  )
}

// Cvent is a deeper sync surface (credentials, sync modes, preview/run). It's
// wired on the admin SPA today; the Console panel is a follow-up — surface the
// status read-only so organizers know where to manage it for now.
function CventCard() {
  return (
    <Card title="Cvent" description="Two-way attendee & session sync.">
      <div className="flex items-center gap-2">
        <Pill>Managed in admin</Pill>
      </div>
      <p className="text-[12.5px] text-plum-700/55 dark:text-plum-200/40">
        Cvent connection, sync modes and preview/run live on the admin workspace
        for now. A full Console panel is a planned follow-up.
      </p>
    </Card>
  )
}
